import { ActionIcon, Anchor, Box, Button, Center, Group, Select, Stack, Text, Tooltip } from '@mantine/core';
import { IconCopy, IconPlus } from '@tabler/icons-react';
import { useInfiniteQuery } from '@tanstack/react-query';
import { DataTable } from 'mantine-datatable';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, type Page } from '../api/client';
import type { Organization } from '../api/types';
import { OrgLogo } from '../components/org-logo';
import { PageActions } from '../components/page-actions';
import { HealthDot, SubscriptionChip, usePlans } from '../components/subscription';
import { useToast } from '../components/Toast';
import { useDebounced } from '../components/useDebounced';
import { nameOf, tr } from '../lib/i18n';
import { fmtAgo, fmtDay, fmtNum, ORG_STATUS, SUBSCRIPTION_STATUS } from '../lib/labels';
import { tableDefaults } from '../ui/DataTable';
import { ErrorState, Panel, SearchInput, StatusBadge, toneOf, Toolbar } from '../ui/kit';
import { OrganizationFormDialog } from './OrganizationFormDialog';

export function OrganizationsPage() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useSearchParams();
  const subscription = search.get('subscription') ?? '';
  const planId = search.get('planId') ?? '';
  const setFilter = (k: string, v: string) => setSearch((s) => { const n = new URLSearchParams(s); if (v) n.set(k, v); else n.delete(k); return n; }, { replace: true });
  const plans = usePlans();
  const [creating, setCreating] = useState(false);
  const dq = useDebounced(q);

  const list = useInfiniteQuery({
    queryKey: ['organizations', dq, status, subscription, planId],
    queryFn: ({ pageParam }) => api<Page<Organization>>('/organizations', { query: { q: dq, status, subscription, planId, cursor: pageParam, limit: 25 } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (p) => p.nextCursor ?? undefined,
  });
  const rows = list.data?.pages.flatMap((p) => p.items) ?? [];
  const copy = (o: Organization) => { void navigator.clipboard.writeText(o.publicUrl); toast(tr('تم نسخ الرابط', 'Link copied')); };

  return (
    <>
      <PageActions>
        <Button leftSection={<IconPlus size={16} />} onClick={() => setCreating(true)}>{tr('شركة جديدة', 'New company')}</Button>
      </PageActions>
      <Panel flush>
        <Toolbar>
          <SearchInput value={q} onChange={(e) => setQ(e.currentTarget.value)} placeholder={tr('ابحث بالاسم أو الرابط أو الهاتف…', 'Search name, link or phone…')} w={{ base: '100%', sm: 300 }} />
          <Select w={{ base: '48%', sm: 170 }} value={status} onChange={(v) => setStatus(v ?? '')}
            data={[{ value: '', label: tr('كل الحالات', 'All statuses') }, ...Object.entries(ORG_STATUS).map(([value, v]) => ({ value, label: v.label }))]} />
          <Select w={{ base: '48%', sm: 170 }} value={subscription} onChange={(v) => setFilter('subscription', v ?? '')}
            data={[{ value: '', label: tr('كل الاشتراكات', 'All subscriptions') }, ...Object.entries(SUBSCRIPTION_STATUS).map(([value, v]) => ({ value, label: v.label }))]} />
          <Select w={{ base: '100%', sm: 160 }} value={planId} onChange={(v) => setFilter('planId', v ?? '')}
            data={[{ value: '', label: tr('كل الباقات', 'All plans') }, ...(plans.data?.items ?? []).map((p) => ({ value: String(p.id), label: nameOf(p) }))]} />
        </Toolbar>
        {list.isError ? (
          <Box p="md"><ErrorState error={list.error} onRetry={() => list.refetch()} /></Box>
        ) : (
          <DataTable<Organization>
            {...(tableDefaults as object)}
            minHeight={rows.length ? undefined : 260}
            fetching={list.isLoading}
            records={rows}
            onRowClick={({ record }) => nav(`/organizations/${record.id}`)}
            rowStyle={() => ({ cursor: 'pointer' })}
            columns={[
              {
                accessor: 'name',
                title: tr('الشركة', 'Company'),
                render: (o) => (
                  <Group gap="sm" wrap="nowrap">
                    <OrgLogo size={34}>{o.name[0]}</OrgLogo>
                    <Box style={{ minWidth: 0 }}>
                      <Text size="sm" fw={600} lh={1.3} truncate>{o.name}{o.name_en ? <Text span c="dimmed" fw={400} size="xs"> · {o.name_en}</Text> : null}</Text>
                      <Text size="xs" c="dimmed" dir="ltr" ta="start" truncate>{o.phone} · {o.email}</Text>
                    </Box>
                  </Group>
                ),
              },
              { accessor: 'status', title: tr('الحالة', 'Status'), render: (o) => <StatusBadge tone={toneOf(ORG_STATUS[o.status]?.color)}>{ORG_STATUS[o.status]?.label}</StatusBadge> },
              {
                accessor: 'subscription',
                title: tr('الاشتراك', 'Subscription'),
                render: (o) => (
                  <Stack gap={2} align="flex-start">
                    <SubscriptionChip status={o.subscriptionStatus} daysLeft={o.daysLeft} planName={o.plan_name} access={o.access} />
                    {o.subscription_ends_at && <Text size="xs" c="dimmed">{tr('حتى', 'Until')} {fmtDay(o.subscription_ends_at)}</Text>}
                  </Stack>
                ),
              },
              {
                accessor: 'health',
                title: tr('النشاط', 'Activity'),
                render: (o) => (
                  <Stack gap={2}>
                    <HealthDot health={o.health} withLabel />
                    <Text size="xs" c="dimmed">{fmtAgo(o.last_login_at)} · {fmtNum(o.activity_30d ?? 0)} {tr('حركة/30ي', 'actions/30d')}</Text>
                  </Stack>
                ),
              },
              {
                accessor: 'slug',
                title: tr('الرابط العام', 'Public link'),
                render: (o) => (
                  <Group gap={4} wrap="nowrap" onClick={(e) => e.stopPropagation()}>
                    <Anchor href={o.publicUrl} target="_blank" size="sm" dir="ltr" ff="monospace" c={o.public_form_enabled ? 'dark' : 'dimmed'} td={o.public_form_enabled ? undefined : 'line-through'}>/{o.slug}</Anchor>
                    <Tooltip label={tr('نسخ الرابط', 'Copy link')}><ActionIcon size="sm" onClick={() => copy(o)} aria-label={tr('نسخ الرابط', 'Copy link')}><IconCopy size={14} /></ActionIcon></Tooltip>
                  </Group>
                ),
              },
              { accessor: 'users_count', title: tr('الموظفون', 'Staff'), textAlign: 'center', render: (o) => <span className="pf-num">{fmtNum(o.users_count)}</span> },
              { accessor: 'properties_count', title: tr('العقارات', 'Properties'), textAlign: 'center', render: (o) => <span className="pf-num">{fmtNum(o.properties_count)}</span> },
              { accessor: 'requests_count', title: tr('الطلبات', 'Requests'), textAlign: 'center', render: (o) => <span className="pf-num">{fmtNum(o.requests_count)}</span> },
              { accessor: 'created_at', title: tr('أُنشئت', 'Created'), render: (o) => <Text size="sm" c="dimmed">{fmtDay(o.created_at)}</Text> },
            ]}
            emptyState={undefined}
            noRecordsText={dq || status || subscription || planId ? tr('لا توجد شركات مطابقة', 'No matching companies') : tr('لا توجد شركات بعد', 'No companies yet')}
          />
        )}
        {list.hasNextPage && (
          <Center p="sm" style={{ borderTop: '1px solid var(--pf-line)' }}>
            <Button variant="default" onClick={() => list.fetchNextPage()} loading={list.isFetchingNextPage}>{tr('عرض المزيد', 'Show more')}</Button>
          </Center>
        )}
      </Panel>
      <OrganizationFormDialog open={creating} onClose={() => setCreating(false)} />
    </>
  );
}
